import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import MainLayout from '@/components/Layout/MainLayout';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import GeenToegang from '@/pages/GeenToegang';
import Overzicht from '@/pages/Overzicht';import LocatieInfo from '@/pages/LocatieInfo';
import Productcatalogus from '@/pages/Productcatalogus';
import Leveranciersbeheer from '@/pages/Leveranciersbeheer';
import Werkprogramma from '@/pages/Werkprogramma';
import Verbruik from '@/pages/Verbruik';
import Schademeldingen from '@/pages/Schademeldingen';
import ChecklistsV2 from '@/pages/ChecklistsV2';
import Rusttaken from '@/pages/Rusttaken';
import Takenbeheer from '@/pages/Takenbeheer';
import Berichtenbeheer from '@/pages/Berichtenbeheer';
import Gebruikersbeheer from '@/pages/Gebruikersbeheer';
import ChecklistTemplatesV2 from '@/pages/ChecklistTemplatesV2';
import EmailInstellingen from '@/pages/management/EmailInstellingen';
import MijnEmailInstellingen from '@/pages/MijnEmailInstellingen';
import ManagementDashboard from '@/pages/management/Dashboard';
import VoorraadOverzicht from '@/pages/management/VoorraadOverzicht';
import TakenOverzicht from '@/pages/management/TakenOverzicht';
import ContractenOverzicht from '@/pages/management/ContractenOverzicht';
import VerbruikOverzicht from '@/pages/management/VerbruikOverzicht';
import SchademeldingenOverzicht from '@/pages/management/SchademeldingenOverzicht';
import ChecklistsOverzicht from '@/pages/management/ChecklistsOverzicht';
import RusttakenOverzicht from '@/pages/management/RusttakenOverzicht';
import BestellingenOverzicht from '@/pages/management/BestellingenOverzicht';
import PerformanceOverzicht from '@/pages/management/PerformanceOverzicht';
import ImplementatiesOverzicht from '@/pages/management/ImplementatiesOverzicht';
import ImplementatieDetail from '@/pages/management/ImplementatieDetail';
import Bestellingen from '@/pages/Bestellingen';
import MijnPerformance from '@/pages/MijnPerformance';
import '@/App.css';

// Protected route wrapper - checks authentication + tool toegang
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, initialized, hasToolAccess } = useAuth();
  const location = useLocation();

  if (!initialized || loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasToolAccess()) {
    return <Navigate to="/geen-toegang" replace />;
  }

  return children;
};

// Admin route wrapper - checks admin role
const AdminRoute = ({ children }) => {
  const { canManage, loading, initialized } = useAuth();

  if (!initialized || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-slate-500">Laden...</p>
        </div>
      </div>
    );
  }

  if (!canManage()) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// E7: pagina's met omzetcijfers zijn bedrijfsleider en hoger. De backend geeft
// 403; de UI stuurt terug naar de startpagina met een duidelijke melding.
const RolRoute = ({ minimaal, children }) => {
  const { user, loading, initialized, getRoleLevel } = useAuth();
  const gemeld = React.useRef(false);
  const mag = getRoleLevel(user?.rol) >= getRoleLevel(minimaal);

  React.useEffect(() => {
    if (initialized && !loading && !mag && !gemeld.current) {
      gemeld.current = true;
      toast.error('Je hebt geen toegang tot deze pagina.');
    }
  }, [initialized, loading, mag]);

  if (!initialized || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!mag) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/geen-toegang" element={<GeenToegang />} />
      
      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Overzicht />} />
        <Route path="performance" element={<RolRoute minimaal="bedrijfsleider"><MijnPerformance /></RolRoute>} />
        <Route path="locatie-info" element={<LocatieInfo />} />
        <Route path="verbruik" element={<Verbruik />} />
        <Route path="schademeldingen" element={<Schademeldingen />} />
        <Route path="checklists" element={<ChecklistsV2 />} />
        <Route path="bestellingen" element={<Bestellingen />} />
        <Route path="rusttaken" element={<Rusttaken />} />
        <Route path="werkprogramma" element={<Werkprogramma />} />
        
        {/* Admin routes - Beheer */}
        <Route path="beheer/producten" element={<AdminRoute><Productcatalogus /></AdminRoute>} />
        <Route path="beheer/leveranciers" element={<AdminRoute><Leveranciersbeheer /></AdminRoute>} />
        <Route path="beheer/taken" element={<AdminRoute><Takenbeheer /></AdminRoute>} />
        <Route path="beheer/berichten" element={<AdminRoute><Berichtenbeheer /></AdminRoute>} />
        <Route path="beheer/gebruikers" element={<AdminRoute><Gebruikersbeheer /></AdminRoute>} />
        <Route path="beheer/checklist-templates" element={<AdminRoute><ChecklistTemplatesV2 /></AdminRoute>} />
        <Route path="beheer/email" element={<AdminRoute><EmailInstellingen /></AdminRoute>} />
        <Route path="mijn-email" element={<MijnEmailInstellingen />} />
        
        {/* Admin routes - Management */}
        <Route path="management" element={<AdminRoute><ManagementDashboard /></AdminRoute>} />
        <Route path="management/voorraad" element={<AdminRoute><VoorraadOverzicht /></AdminRoute>} />
        <Route path="management/taken" element={<AdminRoute><TakenOverzicht /></AdminRoute>} />
        <Route path="management/verbruik" element={<AdminRoute><VerbruikOverzicht /></AdminRoute>} />
        <Route path="management/schademeldingen" element={<AdminRoute><SchademeldingenOverzicht /></AdminRoute>} />
        <Route path="management/checklists" element={<AdminRoute><ChecklistsOverzicht /></AdminRoute>} />
        <Route path="management/rusttaken" element={<AdminRoute><RusttakenOverzicht /></AdminRoute>} />
        <Route path="management/bestellingen" element={<AdminRoute><BestellingenOverzicht /></AdminRoute>} />
        <Route path="management/performance" element={<AdminRoute><PerformanceOverzicht /></AdminRoute>} />
        <Route path="management/implementaties" element={<AdminRoute><ImplementatiesOverzicht /></AdminRoute>} />
        <Route path="management/implementaties/:slug" element={<AdminRoute><ImplementatieDetail /></AdminRoute>} />
        <Route path="management/contracten" element={<AdminRoute><ContractenOverzicht /></AdminRoute>} />
        
        {/* Keep old route for backward compatibility */}
        <Route path="productcatalogus" element={<AdminRoute><Productcatalogus /></AdminRoute>} />
      </Route>
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </>
  );
}

export default App;
